import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { HERO } from "../mock";

const Hero = () => {
  const [offset, setOffset] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative min-h-screen w-full overflow-hidden">
      {/* Background video with parallax */}
      <div
        className="absolute inset-0 -z-10"
        style={{ transform: `translateY(${offset * 0.25}px) scale(1.1)` }}
      >
        <video
          className="w-full h-full object-cover"
          src={HERO.video}
          poster={HERO.bg}
          autoPlay
          muted
          loop
          playsInline
        />
      </div>
      {/* Warm gradient overlays */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#1C2621]/55 via-[#3a4038]/45 to-[#2f4038]/85" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#1C2621]/70 via-transparent to-transparent" />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-28 pb-16">
        <div className="max-w-2xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-4 mb-7 animate-[fadeUp_1s_ease_forwards] opacity-0" style={{ animationDelay: "0.1s" }}>
            <span className="h-px w-10 bg-[#D2703F]" />
            <span className="eyebrow text-[#F4EFE6]/90">{HERO.eyebrow}</span>
          </div>

          {/* Title */}
          <h1 className="font-serif-display text-[#F4EFE6] leading-[0.98]">
            <span className="block italic font-medium text-5xl sm:text-6xl lg:text-[5.2rem] animate-[fadeUp_1s_ease_forwards] opacity-0" style={{ animationDelay: "0.25s" }}>
              {HERO.titleTop}
            </span>
            <span className="block font-extrabold text-6xl sm:text-7xl lg:text-[6.2rem] animate-[fadeUp_1s_ease_forwards] opacity-0" style={{ animationDelay: "0.4s" }}>
              {HERO.titleBottom}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="mt-7 max-w-xl text-[#F4EFE6]/80 text-lg leading-relaxed animate-[fadeUp_1s_ease_forwards] opacity-0" style={{ animationDelay: "0.55s" }}>
            {HERO.subtitle}
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center gap-6 animate-[fadeUp_1s_ease_forwards] opacity-0" style={{ animationDelay: "0.7s" }}>
            <button
              onClick={() => go("#film")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#D2703F] hover:bg-[#B85A2C] text-white font-medium px-8 py-4 transition-all duration-300 shadow-lg shadow-[#D2703F]/30 hover:-translate-y-0.5"
            >
              <Play className="w-4 h-4 fill-current" />
              Watch the film
            </button>
            <button
              onClick={() => go("#experiences")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#D2703F] hover:bg-[#B85A2C] text-white font-medium px-8 py-4 transition-all duration-300 shadow-lg shadow-[#D2703F]/30 hover:-translate-y-0.5"
            >
              Explore Experiences
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => navigate("/diy-guide")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#D2703F] hover:bg-[#B85A2C] text-white font-medium px-8 py-4 transition-all duration-300 shadow-lg shadow-[#D2703F]/30 hover:-translate-y-0.5"
            >
              Do it DIY
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>

          {/* Stats */}
          <div className="mt-16 flex flex-wrap gap-x-10 gap-y-6 animate-[fadeUp_1s_ease_forwards] opacity-0" style={{ animationDelay: "0.85s" }}>
            {HERO.stats.map((s) => (
              <div key={s.value}>
                <div className="font-serif-display text-2xl md:text-3xl text-[#F4EFE6]">
                  {s.value}
                </div>
                <div className="text-[#F4EFE6]/60 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="eyebrow text-[#F4EFE6]/60 text-[0.6rem]">Scroll</span>
        <span className="h-10 w-px bg-gradient-to-b from-[#F4EFE6]/60 to-transparent animate-pulse" />
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
